/**
 * DOCTOR-WORKSPACE-EXPERIENCE-V2 -- the doctor PATIENTS page
 * (`/dashboard/doctor/patients`).
 *
 * Founder ruling 2026-07-20 (the doctor-list reversal): this page LEADS with a
 * LIST of the clinic's patients -- doctor-shaped columns only (name, UMR,
 * gender/age, last-visit; contact MASKED-OR-ABSENT, ZERO clinical column per
 * the CLINICAL WIRE LAW). Each row opens the EXISTING doctor lens. SEARCH stays
 * on this page as a DEMOTED secondary tool beneath the list -- never the hero.
 *
 * The list read is the new doctor-fenced GET /api/doctor/patient-list (DOCTOR +
 * OWN-CLINIC fenced at the SELECT; cross-clinic structurally absent). A
 * multi-clinic doctor's list honors the GLOBAL clinic picker within verified
 * scope (the same narrowing idiom the doctor home + WalkinApp use). Honest
 * loading / error / empty states throughout.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  fetchDoctorPatientList,
  type DoctorPatientListResult,
} from '@/dashboard/api';
import { PageHeader, SectionShell, EmptyState, LoadingState } from '@/dashboard/ui';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useClinicLens } from '@/dashboard/clinic-context';
import DoctorPatientRows, {
  type DoctorPatientRow,
} from '@/dashboard/doctor/DoctorPatientRows';
import { DoctorPatientSearchTool } from '@/dashboard/doctor/DoctorPatientSearchPage';
import {
  DOCTOR_PATIENT_LIST_EMPTY_TITLE,
  DOCTOR_PATIENT_LIST_EMPTY_BODY,
} from '@/dashboard/doctor/doctor-patient-lens-view';

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: DoctorPatientListResult }
  | { kind: 'error'; message: string };

export default function DoctorPatientsPage(): JSX.Element {
  const clinicCtx = useClinicLens();
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2). The
  // lens has already validated its selection against the verified options it
  // was built from, so this surface passes it through rather than re-deriving
  // scope against `me.clinicIds` -- the REACH list, which is empty for an owner
  // and therefore silently discarded the pick.
  const globalPick = clinicCtx?.selectedClinicId ?? null;

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    fetchDoctorPatientList(globalPick ?? undefined)
      .then((data) => setState({ kind: 'ready', data }))
      .catch((e: unknown) =>
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
        }),
      );
  }, [globalPick]);
  useEffect(() => {
    load();
  }, [load]);

  const rows: DoctorPatientRow[] =
    state.kind === 'ready'
      ? state.data.patients.map((p) => ({
          patientId: p.patientId,
          umr: p.umr,
          fullName: p.fullName,
          gender: p.gender,
          ageYears: p.ageYears,
          ageIsApproximate: p.ageIsApproximate,
          lastAt: p.lastVisitAt,
        }))
      : [];

  return (
    <div className="doctor-portal">
      {/* LABEL DEDUP (DOCTOR-WORKSPACE-EXPERIENCE-V2): the page header states the
          SURFACE only; role + clinic live in the ONE chrome location (the app
          top-bar role pill + clinic context). No eyebrow role duplicate. */}
      <PageHeader
        title="Patients"
        subs={[
          'The patients registered at your clinic. Open any patient to review care and continue treatment.',
        ]}
      />

      {/* THE LIST -- the hero of this page (SCOPE 2). */}
      <SectionShell
        eyebrow="Doctor"
        title="Clinic patients"
        sub="Every patient registered at your clinic. Open a patient to review their care."
        testId="doctor-patients-list-section"
      >
        {state.kind === 'loading' ? (
          <LoadingState
            testId="doctor-patients-list-loading"
            label="Loading patients..."
          />
        ) : state.kind === 'error' ? (
          <DashboardLoadError message={state.message} />
        ) : rows.length === 0 ? (
          <EmptyState
            testId="doctor-patients-list-empty"
            title={DOCTOR_PATIENT_LIST_EMPTY_TITLE}
            body={DOCTOR_PATIENT_LIST_EMPTY_BODY}
          />
        ) : (
          <>
            <DoctorPatientRows
              rows={rows}
              lastColumnLabel="Last visit"
              tableTestId="doctor-patients-list-table"
              rowTestId="doctor-patients-list-row"
            />
            {state.data.capped ? (
              <p
                className="text-xs text-muted-foreground mt-2"
                data-testid="doctor-patients-list-capped"
              >
                Showing the {rows.length} most recent registrations. Use search
                below to find a specific patient.
              </p>
            ) : null}
          </>
        )}
      </SectionShell>

      {/* SEARCH -- DEMOTED to a secondary tool beneath the list (SCOPE 2/6). */}
      <DoctorPatientSearchTool />
    </div>
  );
}
